/**
 * Guided Experience API Module
 * API functions for step-by-step coaching system
 * @module api/guided_experience
 */

import { apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// HELPER - Check if user has valid auth token
// =============================================================================
function hasAuthToken() {
  const headers = getAuthHeaders();
  return !!headers['Authorization'];
}

// =============================================================================
// GUIDED EXPERIENCE API
// =============================================================================

export const guidedExperienceAPI = {
  // -------------------------------------------------------------------------
  // WORKFLOWS
  // -------------------------------------------------------------------------

  /**
   * Get all available workflows
   * @param {Object} params - Query params { category, view, featured }
   */
  async getWorkflows(params = {}) {
    // Skip if no auth token (avoid 401 errors)
    if (!hasAuthToken()) {
      return { ok: true, data: [] };
    }

    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/guided-experience/workflows?${queryString}`
        : '/guided-experience/workflows';
      const response = await apiRequest(endpoint);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Get a single workflow with all its steps
   * @param {string} workflowCode - The workflow code
   */
  async getWorkflow(workflowCode) {
    // Skip if no auth token (avoid 401 errors)
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(`/guided-experience/workflows/${workflowCode}`);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Start a workflow for the current user
   * @param {string} workflowCode - The workflow code
   */
  async startWorkflow(workflowCode) {
    // Skip if no auth token (avoid 401 errors)
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(`/guided-experience/workflows/${workflowCode}/start`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Complete a step in a workflow
   * @param {string} workflowCode - The workflow code
   * @param {number} stepOrder - The step order number
   */
  async completeStep(workflowCode, stepOrder) {
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(
        `/guided-experience/workflows/${workflowCode}/step/${stepOrder}`,
        { method: 'POST' }
      );
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Skip/dismiss a workflow
   * @param {string} workflowCode - The workflow code
   */
  async skipWorkflow(workflowCode) {
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(`/guided-experience/workflows/${workflowCode}/skip`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Reset a workflow to allow replaying it
   * @param {string} workflowCode - The workflow code
   */
  async resetWorkflow(workflowCode) {
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(`/guided-experience/workflows/${workflowCode}/reset`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Reset all workflows for the current user (useful for testing/demo)
   */
  async resetAllWorkflows() {
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest('/guided-experience/workflows/reset-all', {
        method: 'POST',
      });
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // -------------------------------------------------------------------------
  // FIELD HINTS
  // -------------------------------------------------------------------------

  /**
   * Get field hints for an entity type
   * @param {string} entityType - The entity type (vehicle, driver, claim, etc.)
   * @param {Object} params - Query params { group }
   */
  async getFieldHints(entityType, params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/guided-experience/field-hints/${entityType}?${queryString}`
        : `/guided-experience/field-hints/${entityType}`;
      const response = await apiRequest(endpoint);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // -------------------------------------------------------------------------
  // COMPLETENESS & REWARDS
  // -------------------------------------------------------------------------

  /**
   * Get reward thresholds for an entity type
   * @param {string} entityType - The entity type
   */
  async getRewards(entityType) {
    try {
      const response = await apiRequest(`/guided-experience/rewards/${entityType}`);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Get cached completeness score for an entity
   * @param {string} entityType - The entity type
   * @param {number} entityId - The entity ID
   */
  async getCompleteness(entityType, entityId) {
    try {
      const response = await apiRequest(`/guided-experience/completeness/${entityType}/${entityId}`);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Calculate/recalculate completeness for an entity
   * @param {string} entityType - The entity type
   * @param {number} entityId - The entity ID
   */
  async calculateCompleteness(entityType, entityId) {
    try {
      const response = await apiRequest(
        `/guided-experience/completeness/${entityType}/${entityId}`,
        { method: 'POST' }
      );
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // -------------------------------------------------------------------------
  // CONTEXTUAL TRIGGERS
  // -------------------------------------------------------------------------

  /**
   * Get relevant triggers for current context
   * @param {Object} params - Query params { view, action }
   */
  async getTriggers(params = {}) {
    if (!hasAuthToken()) {
      return { ok: true, data: [] };
    }

    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = queryString
        ? `/guided-experience/triggers?${queryString}`
        : '/guided-experience/triggers';
      const response = await apiRequest(endpoint);
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  /**
   * Log that a trigger was shown
   * @param {number} triggerId - The trigger ID
   * @param {string} action - The action (shown, dismissed, accepted)
   */
  async logTriggerShown(triggerId, action = 'shown') {
    if (!hasAuthToken()) {
      return { ok: false, error: 'Not authenticated' };
    }

    try {
      const response = await apiRequest(`/guided-experience/triggers/${triggerId}/shown`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },

  // -------------------------------------------------------------------------
  // USER PROGRESS
  // -------------------------------------------------------------------------

  /**
   * Get all workflow progress for current user
   */
  async getProgress() {
    if (!hasAuthToken()) {
      return { ok: true, data: [], stats: {} };
    }

    try {
      const response = await apiRequest('/guided-experience/progress');
      return response;
    } catch (error) {
      return { ok: false, error: error.message };
    }
  },
};

export default guidedExperienceAPI;
